import { memo, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { useLocalParticipant, useTracks } from '@livekit/components-react'
import { Track } from 'livekit-client'

import type { ChatApi } from './chat/useChat'
import { LeaveDialog } from './ConfirmDialog'
import { t } from './i18n'
import type { SurfaceApi } from './surface/useSurface'
import { REACTIONS, type Reaction } from './useClassroomSignals'
import {
  classifyShareError,
  detectCanScreenShare,
  isLiveScreenShare,
  SCREEN_SHARE_CAPTURE,
  SCREEN_SHARE_PUBLISH,
  type ShareFailure,
} from './screenShare'
import type { BackgroundMode, BackgroundStatus } from './useBackgroundEffect'
import type { NoiseFilterApi } from './useNoiseFilter'
import type { PipApi } from './usePictureInPicture'
import type { DeviceKind } from './devices'
import type { DevicesApi } from './useDevices'
import { classifyDeviceError, type DeviceAccess } from './deviceProbe'

/**
 * The class control bar.
 *
 * Sized for children on phones: 52px targets, explicit on/off colour rather than
 * a subtly different icon, and labels available to screen readers. The reaction
 * picker is a popover on desktop and a bottom sheet on mobile, because a
 * horizontal strip of emoji is unusable with a thumb.
 *
 * Teacher-only controls are visually separated so a student never wonders why a
 * button does nothing for them.
 */

interface Props {
  background: {
    mode: BackgroundMode
    busy: boolean
    status: BackgroundStatus
    available: boolean
    degraded: boolean
  }
  /** Manual saver = the user's own sticky choice; auto = the ladder engaged. */
  saverManual: boolean
  saverAuto: boolean
  /** During auto this re-opens the explanation; otherwise it toggles manual. */
  onToggleSaver: () => void
  bgPickerOpen: boolean
  onToggleBgPicker: () => void
  bgButtonRef: React.RefObject<HTMLButtonElement>
  onReaction: (emoji: Reaction) => void
  onCelebrate: () => void
  /** Tutor-mic Krisp; rendered in the teacher group only. */
  noiseFilter: NoiseFilterApi
  /** Floating lesson window; the control hides itself where PiP cannot work. */
  pip: PipApi
  handRaised: boolean
  onToggleHand: () => void
  handQueue: string[]
  onLeave: () => void
  /** Stop another participant's share — the host's take-over path. */
  onStopShare?: (identity: string) => Promise<void>
  /**
   * A share attempt failed for a reason worth explaining. Raised rather than
   * rendered here so the message lands in the one floating toast layer
   * (RoomToasts) instead of growing the control bar mid-lesson. Never called for
   * a cancelled picker.
   */
  onShareFailure?: (failure: ShareFailure) => void
  surface: SurfaceApi
  /** Server-authoritative, so teacher controls appear without waiting for the
   *  LiveKit identity to resolve. */
  isHost: boolean
  /** The participants panel is owned by Classroom, so it can overlay the stage
   *  rather than being trapped inside this bar's backdrop-filter. */
  chatOpen: boolean
  onToggleChat: () => void
  /** The chat STORE — the button subscribes to it for the unread badge, so a
   *  message flood re-renders this one control, never the bar or Classroom. */
  chat: ChatApi
  chatButtonRef: React.RefObject<HTMLButtonElement>
  rosterOpen: boolean
  onToggleRoster: () => void
  participantCount: number
  /** Focus returns here when the panel closes. */
  rosterButtonRef: React.RefObject<HTMLButtonElement>
  devices: DevicesApi
  onDeviceToggleFailed: (kind: 'mic' | 'camera', access: DeviceAccess) => void
  onOpenSetupHub: () => void
  setupHubOpen: boolean
  setupHubButtonRef: React.RefObject<HTMLButtonElement>
}

/**
 * B6 (meet-opt-03) — custom comparator for the React.memo wrapper below.
 *
 * The default `===` would never skip on a Classroom re-render: the underlying
 * objects (`background` from useBackgroundEffect, `surface` from useSurface,
 * `noiseFilter` from useNoiseFilter, `pip` from usePictureInPicture, `chat`
 * from useChat) are freshly-constructed object literals on every hook call,
 * and several callbacks at the call site are inline arrows. The memo would
 * see a new ref for almost every prop and re-render on every parent render.
 *
 * The right comparator compares the FIELDS that actually drive the visible
 * output of the bar — `mode`/`busy`/`status`/`available`/`degraded` instead
 * of `background`, `boardOpen`/`inkArmed` instead of `surface`, `status`
 * instead of `noiseFilter`, `mode`/`active` instead of `pip`, etc. When
 * those are all unchanged the bar's appearance is unchanged, the comparator
 * returns true, and the memo skips — even though every object reference
 * moved. Chat unread is a special case: it is read by `ChatControl` (which
 * has its own `useSyncExternalStore` subscription), not by the bar, so the
 * bar does not need to re-render when unread changes.
 */
function controlBarPropsAreEqual(prev: Props, next: Props): boolean {
  return (
    prev.background.mode === next.background.mode &&
    prev.background.busy === next.background.busy &&
    prev.background.available === next.background.available &&
    prev.background.degraded === next.background.degraded &&
    prev.saverManual === next.saverManual &&
    prev.saverAuto === next.saverAuto &&
    prev.bgPickerOpen === next.bgPickerOpen &&
    prev.chatOpen === next.chatOpen &&
    prev.rosterOpen === next.rosterOpen &&
    prev.handRaised === next.handRaised &&
    prev.handQueue.length === next.handQueue.length &&
    prev.participantCount === next.participantCount &&
    prev.isHost === next.isHost &&
    prev.surface.boardOpen === next.surface.boardOpen &&
    prev.surface.inkArmed === next.surface.inkArmed &&
    prev.noiseFilter.status === next.noiseFilter.status &&
    prev.pip.mode === next.pip.mode &&
    prev.pip.active === next.pip.active &&
    // MEET-OPT-04 — canEnter moves independently of mode/active (a
    // document-mode share start/stop flips it, and a video-mode camera
    // mute/unmute flips it), and the bar renders `disabled={!pip.active &&
    // !pip.canEnter}` directly off it. Without this comparison the memo
    // skipped on a canEnter change and the button went stale (greyed when
    // it would work, or enabled when it was a no-op) until any other
    // compared field moved. With it, all three pip fields the bar reads
    // are compared.
    prev.pip.canEnter === next.pip.canEnter &&
    prev.setupHubOpen === next.setupHubOpen &&
    prev.onOpenSetupHub === next.onOpenSetupHub &&
    prev.devices === next.devices &&
    prev.onDeviceToggleFailed === next.onDeviceToggleFailed
  )
}

export const ControlBar = memo(function ControlBar({
  background,
  saverManual,
  saverAuto,
  onToggleSaver,
  onReaction,
  onCelebrate,
  noiseFilter,
  pip,
  handRaised,
  onToggleHand,
  handQueue,
  onLeave,
  onStopShare,
  onShareFailure,
  surface,
  isHost,
  bgPickerOpen,
  onToggleBgPicker,
  bgButtonRef,
  chatOpen,
  onToggleChat,
  chat,
  chatButtonRef,
  rosterOpen,
  onToggleRoster,
  participantCount,
  rosterButtonRef,
  devices,
  onDeviceToggleFailed,
  onOpenSetupHub,
  setupHubOpen,
  setupHubButtonRef,
}: Props) {
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled } = useLocalParticipant()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [shareBusy, setShareBusy] = useState(false)
  const [confirmLeave, setConfirmLeave] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const [moreMenuStyle, setMoreMenuStyle] = useState<React.CSSProperties>({})
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 640px)').matches)
  const pickerRef = useRef<HTMLDivElement>(null)
  const pipButtonRef = useRef<HTMLButtonElement>(null)
  const pickerButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 640px)')
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])
  const moreRef = useRef<HTMLDivElement>(null)
  const moreButtonRef = useRef<HTMLButtonElement>(null)
  const moreMenuRef = useRef<HTMLDivElement>(null)

  const closeMore = (restoreFocus = false) => {
    setMoreOpen(false)
    if (restoreFocus) moreButtonRef.current?.focus()
  }

  // Derived from the actual publication, not a local flag: the browser's own
  // "Stop sharing" ends the track without telling this component, which used to
  // leave the button stuck looking active.
  // Filtered to LIVE shares: a share the tutor stopped through moderation stays
  // published-and-muted (see isLiveScreenShare), and counting it left this bar
  // believing someone was presenting for the rest of the lesson — which disabled
  // the take-over path and every "ask to share".
  const screenTracks = useTracks([Track.Source.ScreenShare], {
    onlySubscribed: false,
  }).filter(isLiveScreenShare)
  const sharing = screenTracks.some((ref) => ref.participant.isLocal)
  // Someone in the room is presenting (local or remote) — ink only makes sense then.
  const anyShare = screenTracks.length > 0
  // ONE share at a time is product policy (LiveKit would happily carry several,
  // and the stage only renders one). A student cannot start over someone else;
  // the teacher always can, and takes the other share down as they do.
  const otherShare = screenTracks.find((ref) => !ref.participant.isLocal)
  const otherSharer = otherShare
    ? otherShare.participant.name || otherShare.participant.identity
    : null

  const teacher = isHost

  // If share ends while ink is armed, drop ink so we do not leave a ghost layer.
  const inkArmed = surface.inkArmed
  const armInk = surface.armInk
  useEffect(() => {
    if (!anyShare && inkArmed && teacher) {
      armInk(false)
    }
  }, [anyShare, inkArmed, armInk, teacher])

  // Dismiss the picker on any outside interaction or Escape — a popover that
  // traps a child mid-lesson is worse than no popover.
  useEffect(() => {
    if (!pickerOpen) return
    const onPointer = (event: PointerEvent) => {
      if (!pickerRef.current?.contains(event.target as Node)) setPickerOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setPickerOpen(false)
        pickerButtonRef.current?.focus()
      }
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [pickerOpen])

  // The menu is portalled to document.body to escape the control bar's
  // backdrop-filter containing block, so both its DOM node and its trigger are
  // inside this disclosure. A click on an independent toggle must keep it open.
  useEffect(() => {
    if (!moreOpen) return
    const onPointer = (event: PointerEvent) => {
      const target = event.target as Node
      if (
        !moreRef.current?.contains(target) &&
        !moreMenuRef.current?.contains(target)
      ) closeMore()
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !pickerOpen) closeMore(true)
    }
    document.addEventListener('pointerdown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [moreOpen, pickerOpen])

  // A disclosure needs to carry focus into its controls. Unlike a modal this
  // is deliberately not a focus trap: Tab can leave it normally, while Escape
  // and close-on-activate return to the More trigger.
  useEffect(() => {
    if (!moreOpen) return
    const frame = window.requestAnimationFrame(() => {
      moreMenuRef.current
        ?.querySelector<HTMLButtonElement>('.overflow-menu button:not(:disabled)')
        ?.focus()
    })
    return () => window.cancelAnimationFrame(frame)
  }, [moreOpen])

  // Keep the portalled desktop panel tied to the More trigger rather than to
  // the backdrop-filtered control bar. Logical inset positioning preserves the
  // trailing-edge alignment in both LTR and RTL. On the existing mobile
  // breakpoint CSS owns the bottom-sheet geometry instead.
  useLayoutEffect(() => {
    if (!moreOpen) return

    const positionMenu = () => {
      const button = moreButtonRef.current
      if (!button) return

      if (window.matchMedia('(max-width: 640px)').matches) {
        setMoreMenuStyle({})
        return
      }

      const rect = button.getBoundingClientRect()
      const inlineEnd = document.documentElement.dir === 'rtl'
        ? rect.left
        : window.innerWidth - rect.right

      setMoreMenuStyle({
        bottom: Math.max(12, window.innerHeight - rect.top + 12),
        insetInlineEnd: Math.max(8, inlineEnd),
      })
    }

    positionMenu()
    window.addEventListener('resize', positionMenu)
    window.addEventListener('scroll', positionMenu, true)
    return () => {
      window.removeEventListener('resize', positionMenu)
      window.removeEventListener('scroll', positionMenu, true)
    }
  }, [moreOpen])

  const toggleShare = async () => {
    setShareBusy(true)
    try {
      // Take-over stops the other share — but NOT awaited first. The stop is an
      // HTTP round-trip, and awaiting it here spent the tap's transient
      // activation, so the tutor's own picker then refused to open. Fire it and
      // go straight to the gated call; the brief overlap is harmless because
      // Stage prefers the teacher's share, and the zombie track is gone a
      // moment later.
      if (!sharing && otherShare && teacher && onStopShare) {
        // EVERY other share: a race can leave two, and stopping just the first
        // found kept a zombie burning a child's uplink.
        for (const ref of screenTracks) {
          if (!ref.participant.isLocal) {
            void onStopShare(ref.participant.identity).catch(() => undefined)
          }
        }
      }
      const turningOn = !sharing
      await localParticipant?.setScreenShareEnabled(
        !sharing,
        SCREEN_SHARE_CAPTURE,
        SCREEN_SHARE_PUBLISH,
      )
      if (turningOn && !pip.active && pip.canEnter) pip.toggle()
    } catch (error) {
      // State is derived, so nothing needs unwinding — but SOMETHING has to be
      // said. This used to be an empty catch, which made a genuine refusal
      // indistinguishable from pressing Cancel: the button span and returned to
      // idle either way. `cancelled` stays silent because dismissing a picker is
      // an intention, not a fault; everything else gets words.
      const failure = classifyShareError(error)
      if (failure !== 'cancelled') onShareFailure?.(failure)
    } finally {
      setShareBusy(false)
    }
  }

  // Not a bare getDisplayMedia check: Android defines the method and then
  // refuses every call, so the button would exist and never work.
  const screenShareSupported = detectCanScreenShare()

  // role="toolbar" promises arrow-key movement between its controls (WAI-ARIA
  // toolbar pattern) — this delivers the simple version: Left/Right walk the
  // enabled buttons (direction-aware for RTL), Home/End jump to the ends. Tab
  // order is untouched, so this only ADDS a path; nothing existing changes.
  const onToolbarKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    // Once the disclosure is open, its independent toggles use ordinary Tab
    // navigation. Arrow/Home/End remain scoped to the visible toolbar only.
    if (moreOpen) return
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
    const target = event.target as HTMLElement
    if (target.tagName !== 'BUTTON') return
    const buttons = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'))
    const index = buttons.indexOf(target as HTMLButtonElement)
    if (index < 0) return
    let next = index
    if (event.key === 'Home') next = 0
    else if (event.key === 'End') next = buttons.length - 1
    else {
      // In RTL "forward along the bar" is ArrowLeft — follow the writing
      // direction, not the physical key.
      const forward = (event.key === 'ArrowRight') !== (document.documentElement.dir === 'rtl')
      next = index + (forward ? 1 : -1)
    }
    if (next < 0 || next >= buttons.length || next === index) return
    event.preventDefault()
    buttons[next].focus()
  }

  const moreHasActivity =
    saverManual ||
    saverAuto ||
    background.mode !== 'off' ||
    handQueue.length > 0 ||
    noiseFilter.status === 'on' ||
    pip.active
  const moreLabel = handQueue.length > 0
    ? t(moreOpen ? 'more.closeHands' : 'more.openHands').replace('{count}', String(handQueue.length))
    : moreHasActivity
      ? t(moreOpen ? 'more.closeUpdates' : 'more.openUpdates')
      : t(moreOpen ? 'more.close' : 'more.open')
  const deviceKinds = (['audioinput', 'videoinput', 'audiooutput'] as const).filter(
    (kind) =>
      devices.devices[kind].length > 1 &&
      (kind !== 'audiooutput' || devices.supportsSpeakerSelection),
  )
  const deviceHeading = (kind: DeviceKind) =>
    kind === 'audioinput' ? t('devices.microphone') : kind === 'videoinput' ? t('devices.camera') : t('devices.speaker')
  const deviceAction = (kind: DeviceKind) =>
    kind === 'audioinput' ? 'devices.useMic' : kind === 'videoinput' ? 'devices.useCamera' : 'devices.useSpeaker'

  // LK-010: the noise-cancellation pill, rendered in the teacher section for
  // hosts and the general section for students. The status gate hides it
  // wherever the filter is unavailable — for hosts under the current policy
  // that is everywhere, so their view is unchanged.
  const noisePill =
    noiseFilter.status !== 'unsupported' && noiseFilter.status !== 'unavailable' ? (
      <button
        type="button"
        className={`overflow-menu__item ${noiseFilter.status === 'on' ? 'overflow-menu__item--active' : ''} ${noiseFilter.status === 'failed' ? 'overflow-menu__item--warn' : ''}`}
        onClick={noiseFilter.toggle}
        aria-pressed={noiseFilter.status === 'on'}
        aria-busy={noiseFilter.status === 'loading'}
      >
        <span className="overflow-menu__icon" aria-hidden="true"><NoiseIcon /></span>
        <span className="overflow-menu__label">{t('nc.control')}</span>
        <span className={`pill-button__dot pill-button__dot--${noiseFilter.status}`} aria-hidden="true" />
        <span className="overflow-menu__meta">
          {t(
            noiseFilter.status === 'on'
              ? 'nc.stateOn'
              : noiseFilter.status === 'loading'
                ? 'nc.stateLoading'
                : noiseFilter.status === 'failed'
                  ? 'nc.stateFailed'
                  : 'nc.stateOff',
          )}
        </span>
      </button>
    ) : null

  return (
    <div
      className="control-bar"
      role="toolbar"
      aria-label={t('room.controls')}
    >
      {/* Primary — the 6 controls a child uses every minute. Everything else
          lives under "More" so the bar never wraps and the video never jumps. */}
      <div className="control-bar__primary" onKeyDown={onToolbarKeyDown}>
        <Control
          on={isMicrophoneEnabled}
          // Failed enable attempts are classified in Classroom, where the
          // walkthrough and the device-access signal already live.
          onClick={() => {
            const enabled = !isMicrophoneEnabled
            void localParticipant?.setMicrophoneEnabled(enabled).catch((error) => {
              if (enabled) onDeviceToggleFailed('mic', classifyDeviceError(error).access)
              else console.debug('[media] microphone disable failed', error)
            })
          }}
          label={t(isMicrophoneEnabled ? 'prejoin.micOn' : 'prejoin.micOff')}
          caption={t('ctl.mic')}
          danger={!isMicrophoneEnabled}
        >
          {isMicrophoneEnabled ? <MicIcon /> : <MicOffIcon />}
        </Control>

        <Control
          on={isCameraEnabled}
          // A failed enable opens the camera walkthrough; failed turn-off stays
          // honest and only writes a debug record.
          onClick={() => {
            const enabled = !isCameraEnabled
            void localParticipant?.setCameraEnabled(enabled).catch((error) => {
              if (enabled) onDeviceToggleFailed('camera', classifyDeviceError(error).access)
              else console.debug('[media] camera disable failed', error)
            })
          }}
          label={t(isCameraEnabled ? 'prejoin.camOn' : 'prejoin.camOff')}
          caption={t('ctl.camera')}
          danger={!isCameraEnabled}
        >
          {isCameraEnabled ? <CamIcon /> : <CamOffIcon />}
        </Control>

        <Control
          on={handRaised}
          onClick={onToggleHand}
          label={t(handRaised ? 'room.lowerHand' : 'room.raiseHand')}
          caption={t('ctl.hand')}
          accent={handRaised}
        >
          <span aria-hidden="true">✋</span>
        </Control>

        {screenShareSupported ? (
          <Control
            on={sharing}
            onClick={toggleShare}
            caption={t('ctl.share')}
            label={
              sharing
                ? t('room.stopShare')
                : otherSharer
                  ? teacher
                    ? t('share.takeOver')
                    : t('share.otherPresenting').replace('{name}', otherSharer)
                  : t('room.share')
            }
            accent={sharing}
            busy={shareBusy}
            disabled={!sharing && Boolean(otherSharer) && !teacher}
          >
            {shareBusy ? <Spinner /> : <ShareIcon />}
          </Control>
        ) : null}

        {isHost && pip.mode !== 'none' && !isMobile ? (
          <Control
            on={pip.active}
            onClick={() => {
              pip.toggle()
            }}
            label={t(pip.active ? 'pip.exit' : 'pip.enter')}
            caption={t('ctl.pip')}
            accent={pip.active}
            disabled={!pip.active && !pip.canEnter}
            buttonRef={pipButtonRef}
          >
            <PipIcon />
          </Control>
        ) : null}

        <ChatControl
          chat={chat}
          open={chatOpen}
          onToggle={onToggleChat}
          buttonRef={chatButtonRef}
        />

        <Control
          on={rosterOpen}
          onClick={onToggleRoster}
          caption={t('ctl.people')}
          label={
            rosterOpen
              ? t('roster.hide')
              : participantCount > 0
                ? t('roster.openCount').replace('{count}', String(participantCount))
                : t('roster.open')
          }
          accent={rosterOpen}
          expanded={rosterOpen}
          buttonRef={rosterButtonRef}
        >
          <PeopleIcon />
          {participantCount > 0 ? (
            <span className="control__count" aria-hidden="true">
              {participantCount}
            </span>
          ) : null}
        </Control>

        <div className="control-bar__divider" aria-hidden="true" />

        <div className="control-bar__more-anchor" ref={moreRef}>
          <Control
            on={moreOpen}
            onClick={() => setMoreOpen((v) => !v)}
            label={moreLabel}
            caption={t('more.caption')}
            accent={moreOpen}
            expanded={moreOpen}
            hasPopup
            pressed={false}
            buttonRef={moreButtonRef}
          >
            <MoreIcon />
            {handQueue.length > 0 ? (
              <span className="control__count control__count--more" aria-hidden="true">
                {handQueue.length}
              </span>
            ) : moreHasActivity ? (
              <span className="control__dot" aria-hidden="true" />
            ) : null}
          </Control>

          {moreOpen ? createPortal(
            <div
              ref={moreMenuRef}
              className="overflow-menu"
              id="more-controls"
              role="region"
              aria-label={t('more.controls')}
              style={moreMenuStyle}
            >
              {/* Student / general tools */}
              <div className="overflow-menu__section" role="group" aria-labelledby="more-tools-heading">
                <h2 className="overflow-menu__heading" id="more-tools-heading">{t('more.tools')}</h2>

                <button type="button" className={`overflow-menu__item ${setupHubOpen ? 'overflow-menu__item--active' : ''}`} onClick={onOpenSetupHub} aria-pressed={setupHubOpen} ref={setupHubButtonRef}>
                  <span className="overflow-menu__icon" aria-hidden="true"><HelpIcon /></span>
                  <span className="overflow-menu__label">{t('help.menu')}</span>
                </button>

                <button
                  type="button"
                  className={`overflow-menu__item ${saverManual || saverAuto ? 'overflow-menu__item--active' : ''}`}
                  onClick={() => {
                    onToggleSaver()
                  }}
                  aria-pressed={saverManual || saverAuto}
                >
                  <span className="overflow-menu__icon" aria-hidden="true">
                    <SaverIcon />
                  </span>
                  <span className="overflow-menu__label">
                    {t(saverManual ? 'saver.controlOn' : saverAuto ? 'saver.controlAuto' : 'saver.control')}
                  </span>
                  {(saverManual || saverAuto) && <span className="overflow-menu__check" aria-hidden="true">✓</span>}
                </button>

                {/* LK-004/LK-020: the control NEVER disappears silently.
                    Supported-but-degraded (canvas fallback) stays enabled — the
                    picker's own `bg.slow` note does the explaining — and a
                    browser without background support gets a disabled entry
                    carrying the reason instead of a vanished mystery.
                    Visibility only; canAutoEnableSegmentation auto-default
                    logic is untouched. */}
                {background.available ? (
                  <button
                    type="button"
                    className={`overflow-menu__item ${background.mode !== 'off' ? 'overflow-menu__item--active' : ''}`}
                    onClick={() => onToggleBgPicker()}
                    aria-pressed={background.mode !== 'off'}
                    aria-expanded={bgPickerOpen}
                    ref={bgButtonRef as unknown as React.RefObject<HTMLButtonElement>}
                  >
                    <span className="overflow-menu__icon" aria-hidden="true">
                      {background.busy ? <Spinner /> : <BlurIcon />}
                    </span>
                    <span className="overflow-menu__label">{t(bgPickerOpen ? 'bg.close' : 'bg.open')}</span>
                    {background.mode !== 'off' && <span className="overflow-menu__check" aria-hidden="true">✓</span>}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="overflow-menu__item overflow-menu__item--reason"
                    disabled
                    title={t('bg.unsupported')}
                  >
                    <span className="overflow-menu__icon" aria-hidden="true">
                      <BlurIcon />
                    </span>
                    <span className="overflow-menu__label">{t('bg.unsupported')}</span>
                  </button>
                )}

                <div className="overflow-menu__item-wrapper picker-anchor" ref={pickerRef}>
                  <button
                    type="button"
                    className={`overflow-menu__item ${pickerOpen ? 'overflow-menu__item--active' : ''}`}
                    onClick={() => setPickerOpen((v) => !v)}
                    aria-pressed={pickerOpen}
                    aria-expanded={pickerOpen}
                    ref={pickerButtonRef}
                  >
                    <span className="overflow-menu__icon" aria-hidden="true">
                      <SmileIcon />
                    </span>
                    <span className="overflow-menu__label">{t(pickerOpen ? 'room.reactClose' : 'room.react')}</span>
                  </button>
                  {pickerOpen ? (
                    <div className="picker picker--in-menu" role="group" aria-label={t('room.react')}>
                      {REACTIONS.map((emoji) => (
                        <button
                          key={emoji}
                          type="button"
                          className="picker__item"
                          aria-label={emoji}
                          onClick={() => {
                            onReaction(emoji)
                            setPickerOpen(false)
                          }}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>

                {pip.mode !== 'none' && (isMobile || !isHost) ? (
                  <button
                    type="button"
                    className={`overflow-menu__item ${pip.active ? 'overflow-menu__item--active' : ''}`}
                    onClick={pip.toggle}
                    aria-pressed={pip.active}
                    disabled={!pip.active && !pip.canEnter}
                  >
                    <span className="overflow-menu__icon" aria-hidden="true">
                      <PipIcon />
                    </span>
                    <span className="overflow-menu__label">{t(pip.active ? 'pip.exit' : 'pip.enter')}</span>
                  </button>
                ) : null}

                {/* LK-010: students get the opt-in noise-cancellation pill in
                    the general section — RNNoise only, never Krisp (see
                    useNoiseFilter). Hosts see it in the teacher section. */}
                {!teacher ? noisePill : null}
              </div>

              {deviceKinds.length > 0 ? (
                <div className="overflow-menu__section" role="group" aria-labelledby="more-devices-heading">
                  <h2 className="overflow-menu__heading" id="more-devices-heading">{t('devices.heading')}</h2>
                  {deviceKinds.map((kind) => (
                    <div className="overflow-menu__device-kind" key={kind} aria-busy={devices.switching === kind || undefined}>
                      <h3 className="overflow-menu__subheading">{deviceHeading(kind)}</h3>
                      {devices.devices[kind].map((device) => {
                        const label = device.label || t('devices.systemDefault')
                        const selected = devices.active[kind] === device.deviceId
                        return (
                          <button
                            key={device.deviceId}
                            type="button"
                            className={`overflow-menu__item ${selected ? 'overflow-menu__item--active' : ''}`}
                            onClick={() => void devices.select(kind, device.deviceId)}
                            aria-pressed={selected}
                            aria-busy={devices.switching === kind || undefined}
                            aria-label={t(deviceAction(kind) as Parameters<typeof t>[0]).replace('{device}', label)}
                            disabled={devices.switching !== null}
                          >
                            <span className="overflow-menu__icon" aria-hidden="true">
                              {kind === 'audioinput' ? <MicIcon /> : kind === 'videoinput' ? <CamIcon /> : <SpeakerIcon />}
                            </span>
                            <span className="overflow-menu__label"><bdi dir="auto">{label}</bdi></span>
                            {selected ? <span className="overflow-menu__check" aria-hidden="true">✓</span> : null}
                          </button>
                        )
                      })}
                    </div>
                  ))}
                </div>
              ) : null}

              {teacher ? (
                <div className="overflow-menu__section overflow-menu__section--teacher" role="group" aria-labelledby="more-teacher-heading">
                  <h2 className="overflow-menu__heading" id="more-teacher-heading">{t('role.teacher')}</h2>

                  <button
                    type="button"
                    className={`overflow-menu__item ${surface.boardOpen ? 'overflow-menu__item--active' : ''}`}
                    onClick={() => surface.boardOpen ? surface.closeBoard() : surface.openBoard()}
                    aria-pressed={surface.boardOpen}
                  >
                    <span className="overflow-menu__icon" aria-hidden="true"><BoardIcon /></span>
                    <span className="overflow-menu__label">
                      {t(surface.boardOpen ? 'surface.closeBoard' : 'surface.board')}
                    </span>
                  </button>

                  {(anyShare || surface.inkArmed) && (
                    <button
                      type="button"
                      className={`overflow-menu__item ${surface.inkArmed ? 'overflow-menu__item--active' : ''}`}
                      onClick={() => surface.armInk(!surface.inkArmed)}
                      aria-pressed={surface.inkArmed}
                    >
                      <span className="overflow-menu__icon" aria-hidden="true"><InkIcon /></span>
                      <span className="overflow-menu__label">
                        {t(surface.inkArmed ? 'surface.stopInk' : 'surface.ink')}
                      </span>
                    </button>
                  )}

                  <button
                    type="button"
                    className="overflow-menu__item overflow-menu__item--celebrate"
                    onClick={() => {
                      onCelebrate()
                      closeMore(true)
                    }}
                  >
                    <span className="overflow-menu__icon" aria-hidden="true"><CelebrateIcon /></span>
                    <span className="overflow-menu__label">{t('room.celebrate')}</span>
                  </button>

                  {noisePill}

                  {handQueue.length > 0 ? (
                    <div className="overflow-menu__hands" role="status" aria-live="polite">
                      ✋ {handQueue.length} {t('room.handsUp')}
                    </div>
                  ) : null}
                </div>
              ) : null}

              <button
                type="button"
                className="overflow-menu__item overflow-menu__item--leave-mobile"
                onClick={() => {
                  closeMore(true)
                  setConfirmLeave(true)
                }}
              >
                <span className="overflow-menu__icon" aria-hidden="true"><LeaveIcon /></span>
                <span className="overflow-menu__label">{t('room.leave')}</span>
              </button>
            </div>,
            document.body,
          ) : null}
        </div>
      </div>

      <div className="control-bar__end" onKeyDown={onToolbarKeyDown}>
        <button type="button" className="pill-button pill-button--danger control-bar__leave" onClick={() => setConfirmLeave(true)} aria-label={t('room.leave')}>
          <LeaveIcon />
          <span className="control-bar__leave-label">{t('room.leave')}</span>
        </button>
      </div>

      {confirmLeave ? <LeaveDialog onConfirm={onLeave} onCancel={() => setConfirmLeave(false)} /> : null}
    </div>
  )
}, controlBarPropsAreEqual)


/**
 * The chat button owns its own subscription to the chat store: the unread badge
 * updates without re-rendering the control bar (let alone Classroom). This is
 * the second half of keeping a message flood off the video tree.
 */
function ChatControl({
  chat,
  open,
  onToggle,
  buttonRef,
}: {
  chat: ChatApi
  open: boolean
  onToggle: () => void
  buttonRef: React.RefObject<HTMLButtonElement>
}) {
  const snap = useSyncExternalStore(chat.subscribe, chat.getSnapshot)
  const unread = snap.unread
  return (
    <>
      <Control
        on={open}
        onClick={onToggle}
        caption={t('ctl.chat')}
        // The label is the ACTION, in both directions: an icon button that says
        // "Open chat" while the chat is open is the classic toggle-label bug.
        label={
          open
            ? t('chat.close')
            : unread > 0
              ? t('chat.openUnread').replace('{count}', String(unread))
              : t('chat.open')
        }
        accent={open}
        expanded={open}
        buttonRef={buttonRef}
      >
        <ChatIcon />
        {unread > 0 ? (
          <span className="control__count control__count--chat" aria-hidden="true">
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </Control>
      {/* The badge is aria-hidden and a label change on an unfocused button is
          not re-announced — this quiet region is what a screen reader hears.
          Text only when non-zero, so opening the panel announces nothing. */}
      <span className="visually-hidden" aria-live="polite">
        {unread > 0 ? t('chat.openUnread').replace('{count}', String(unread)) : ''}
      </span>
    </>
  )
}

function Control({
  on,
  onClick,
  label,
  caption,
  children,
  danger,
  accent,
  busy,
  disabled,
  expanded,
  hasPopup,
  pressed = true,
  buttonRef,
}: {
  on: boolean
  onClick: () => void
  label: string
  /** One-word text under the circle — the "what is this button" answer. */
  caption?: string
  children: React.ReactNode
  danger?: boolean
  accent?: boolean
  busy?: boolean
  disabled?: boolean
  expanded?: boolean
  hasPopup?: boolean
  /** A disclosure has aria-expanded, not a second pressed state. */
  pressed?: boolean
  buttonRef?: React.RefObject<HTMLButtonElement>
}) {
  const button = (
    <button
      type="button"
      className={[
        'control',
        danger ? 'control--danger' : '',
        accent && on ? 'control--accent' : '',
        busy ? 'control--busy' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      ref={buttonRef}
      onClick={onClick}
      aria-pressed={pressed ? on : undefined}
      aria-label={label}
      aria-busy={busy || undefined}
      aria-expanded={expanded}
      aria-haspopup={hasPopup || undefined}
      // data-tip drives the CSS tooltip (hover/focus, desktop). The native
      // title is GONE on purpose: it double-tooltips next to ours, appears on
      // its own lazy schedule, and never shows for keyboard focus.
      data-tip={label}
      disabled={busy || disabled}
    >
      {children}
    </button>
  )
  if (!caption) return button
  return (
    <span className="control-wrap">
      {button}
      {/* aria-hidden: the button's aria-label already says this and more —
          announcing both reads every control twice. */}
      <span className="control__caption" aria-hidden="true">
        {caption}
      </span>
    </span>
  )
}

const Spinner = () => <span className="spinner" aria-hidden="true" />

const MicIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V22h2v-3.08A7 7 0 0 0 19 12h-2Z" />
  </svg>
)
const MicOffIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M15 10.6V6a3 3 0 0 0-5.94-.6l5.94 5.2ZM4.3 3 3 4.3l6 5.3V12a3 3 0 0 0 4.6 2.55l1.4 1.23A5 5 0 0 1 7 12H5a7 7 0 0 0 6 6.92V22h2v-3.08a7 7 0 0 0 3.2-1.35L19.7 20 21 18.7 4.3 3Z" />
  </svg>
)
const CamIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M17 10.5V7a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-3.5l4 4v-11l-4 4Z" />
  </svg>
)
const CamOffIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M4.3 3 3 4.3 5.7 7H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h12a1 1 0 0 0 .9-.6L19.7 21 21 19.7 4.3 3ZM17 8.5V7a1 1 0 0 0-1-1H9.5l7.5 7.4V13l4 4V6l-4 2.5Z" />
  </svg>
)
const SpeakerIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M4 9v6h4l5 4V5L8 9H4Zm12.5 3a4.5 4.5 0 0 0-2.2-3.87v7.74A4.5 4.5 0 0 0 16.5 12Zm-2.2-8.03v2.28a6.5 6.5 0 0 1 0 11.5v2.28a8.5 8.5 0 0 0 0-16.06Z" />
  </svg>
)
/** Signal bars, two lit: "use less data". Matches the tile badge glyph. */
const SaverIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M3 20h3.4v-6H3v6Zm5.8 0h3.4V9H8.8v11Z" />
    <path d="M14.6 20H18V4h-3.4v16Zm5.7 0H23v-9h-2.7v9Z" opacity="0.35" />
  </svg>
)
const BlurIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18Zm0 2.2a6.8 6.8 0 0 1 0 13.6V5.2Z" />
    <circle cx="7" cy="9" r="1.1" />
    <circle cx="7" cy="15" r="1.1" />
    <circle cx="5.2" cy="12" r="1.1" />
  </svg>
)
const ChatIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M4 4h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H8l-5 4V5a1 1 0 0 1 1-1Zm3 5h10v-2H7v2Zm0 4h7v-2H7v2Z" />
  </svg>
)

const PeopleIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M16 11a3 3 0 1 0-3-3 3 3 0 0 0 3 3Zm-8 0a3 3 0 1 0-3-3 3 3 0 0 0 3 3Zm0 2c-2.3 0-6 1.2-6 3.5V19h8v-2.5c0-.9.4-1.7 1-2.4A10 10 0 0 0 8 13Zm8 0c-.3 0-.7 0-1.1.05A4 4 0 0 1 16 16.5V19h6v-2.5c0-2.3-3.7-3.5-6-3.5Z" />
  </svg>
)

const SmileIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm-3.5 7a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm7 0a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 18a5.5 5.5 0 0 1-4.9-3h9.8A5.5 5.5 0 0 1 12 18Z" />
  </svg>
)
const ShareIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M20 4H4a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h6v2H8v2h8v-2h-2v-2h6a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2Zm0 12H4V6h16v10Z" />
  </svg>
)
/** A frame with a smaller frame popped toward the corner. */
const PipIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
    <path d="M21 3H3a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2Zm0 16H3V5h18v14Z" />
    <path d="M12.5 11.5H19V17h-6.5v-5.5Z" />
  </svg>
)

const HelpIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <circle cx="12" cy="12" r="8" />
    <path d="M9.8 9.5a2.3 2.3 0 1 1 3.7 1.8c-.9.7-1.5 1.1-1.5 2.2M12 17h.01" strokeLinecap="round" />
  </svg>
)

const MoreIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor" aria-hidden="true">
    <circle cx="6" cy="12" r="2.2" />
    <circle cx="12" cy="12" r="2.2" />
    <circle cx="18" cy="12" r="2.2" />
  </svg>
)

const BoardIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <rect x="4" y="3" width="16" height="15" rx="2" />
    <path d="M8 8h8M8 12h5M9 21h6M12 18v3" />
  </svg>
)

const InkIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m14.5 4.5 5 5L10 19l-5 1 1-5 8.5-10.5Z" />
    <path d="m12.5 6.5 5 5M4 21h16" />
  </svg>
)

const CelebrateIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="m4 20 6-2 8-8-4-4-8 8-2 6Z" />
    <path d="m14 4 1-2m3 5 2-1m-1 5 2 1M8 7 6 5" />
  </svg>
)

const NoiseIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M6 11a6 6 0 0 0 12 0M12 17v4M9 21h6M3 8v6m18-6v6" />
  </svg>
)

const LeaveIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 5H5v14h5M14 8l4 4-4 4M18 12H9" />
  </svg>
)
